import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { useData } from "@/lib/useData";
import type { Deal, Company, Contact, DealStage } from "@/lib/types";
import { DEAL_STAGES } from "@/lib/types";
import { Plus, MoreHorizontal, Pencil, Trash2, DollarSign } from "lucide-react";

const stageColors: Record<string, string> = {
	lead: "bg-slate-200 text-slate-800",
	qualified: "bg-blue-100 text-blue-800",
	proposal: "bg-amber-100 text-amber-800",
	negotiation: "bg-purple-100 text-purple-800",
	closed_won: "bg-green-100 text-green-800",
	closed_lost: "bg-red-100 text-red-800",
};

function formatCurrency(value: number) {
	return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value);
}

const emptyDeal = {
	title: "",
	value: 0,
	stage: "lead" as DealStage,
	companyId: null as string | null,
	contactId: null as string | null,
	description: "",
	expectedCloseDate: "",
};

export function DealsPage() {
	const dealsFetcher = useCallback(() => api.deals.list(), []);
	const companiesFetcher = useCallback(() => api.companies.list(), []);
	const contactsFetcher = useCallback(() => api.contacts.list(), []);

	const { data: deals, loading, refresh } = useData<Deal[]>(dealsFetcher);
	const { data: companies } = useData<Company[]>(companiesFetcher);
	const { data: contacts } = useData<Contact[]>(contactsFetcher);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState<Deal | null>(null);
	const [form, setForm] = useState(emptyDeal);

	const companyMap = new Map(companies?.map(c => [c.id, c.name]) ?? []);
	const contactMap = new Map(contacts?.map(c => [c.id, `${c.firstName} ${c.lastName}`]) ?? []);

	// Group deals by stage for pipeline view
	const pipeline = DEAL_STAGES.filter(s => !["closed_won", "closed_lost"].includes(s.value));
	const closedStages = DEAL_STAGES.filter(s => ["closed_won", "closed_lost"].includes(s.value));

	function openCreate() {
		setEditing(null);
		setForm(emptyDeal);
		setDialogOpen(true);
	}

	function openEdit(deal: Deal) {
		setEditing(deal);
		setForm({
			title: deal.title,
			value: deal.value,
			stage: deal.stage,
			companyId: deal.companyId,
			contactId: deal.contactId,
			description: deal.description,
			expectedCloseDate: deal.expectedCloseDate,
		});
		setDialogOpen(true);
	}

	async function handleSave() {
		if (editing) {
			await api.deals.update(editing.id, form);
		} else {
			await api.deals.create(form);
		}
		setDialogOpen(false);
		refresh();
	}

	async function handleDelete(id: string) {
		await api.deals.delete(id);
		refresh();
	}

	async function handleStageChange(dealId: string, stage: DealStage) {
		await api.deals.update(dealId, { stage });
		refresh();
	}

	if (loading) {
		return <div className="p-6 text-muted-foreground">Loading...</div>;
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Deals</h1>
					<p className="text-sm text-muted-foreground">{deals?.length ?? 0} total deals</p>
				</div>
				<Button onClick={openCreate} size="sm">
					<Plus className="h-4 w-4 mr-1" /> Add Deal
				</Button>
			</div>

			{/* Pipeline View */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
				{pipeline.map(stage => {
					const stageDeals = deals?.filter(d => d.stage === stage.value) ?? [];
					const stageTotal = stageDeals.reduce((sum, d) => sum + d.value, 0);

					return (
						<div key={stage.value} className="space-y-3">
							<div className="flex items-center justify-between">
								<Badge variant="secondary" className={stageColors[stage.value]}>
									{stage.label}
								</Badge>
								<span className="text-xs text-muted-foreground">{formatCurrency(stageTotal)}</span>
							</div>
							<div className="space-y-2">
								{stageDeals.length === 0 ? (
									<Card className="border-dashed">
										<CardContent className="p-4 text-center">
											<p className="text-xs text-muted-foreground">No deals</p>
										</CardContent>
									</Card>
								) : (
									stageDeals.map(deal => (
										<Card key={deal.id} className="group">
											<CardContent className="p-4">
												<div className="flex items-start justify-between">
													<div className="space-y-1 min-w-0 flex-1">
														<p className="text-sm font-medium truncate">{deal.title}</p>
														<div className="flex items-center gap-1 text-xs text-muted-foreground">
															<DollarSign className="h-3 w-3" />
															{formatCurrency(deal.value)}
														</div>
														{deal.companyId && (
															<p className="text-xs text-muted-foreground truncate">
																{companyMap.get(deal.companyId)}
															</p>
														)}
													</div>
													<DropdownMenu>
														<DropdownMenuTrigger asChild>
															<Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity">
																<MoreHorizontal className="h-3.5 w-3.5" />
															</Button>
														</DropdownMenuTrigger>
														<DropdownMenuContent align="end">
															<DropdownMenuItem onClick={() => openEdit(deal)}>
																<Pencil className="h-4 w-4 mr-2" /> Edit
															</DropdownMenuItem>
															{DEAL_STAGES.filter(s => s.value !== deal.stage).map(s => (
																<DropdownMenuItem key={s.value} onClick={() => handleStageChange(deal.id, s.value)}>
																	Move to {s.label}
																</DropdownMenuItem>
															))}
															<DropdownMenuItem className="text-destructive" onClick={() => handleDelete(deal.id)}>
																<Trash2 className="h-4 w-4 mr-2" /> Delete
															</DropdownMenuItem>
														</DropdownMenuContent>
													</DropdownMenu>
												</div>
											</CardContent>
										</Card>
									))
								)}
							</div>
						</div>
					);
				})}
			</div>

			{/* Closed Deals */}
			{closedStages.map(stage => {
				const stageDeals = deals?.filter(d => d.stage === stage.value) ?? [];
				if (stageDeals.length === 0) return null;
				return (
					<Card key={stage.value}>
						<CardHeader className="pb-3">
							<CardTitle className="text-base flex items-center gap-2">
								<Badge variant="secondary" className={stageColors[stage.value]}>
									{stage.label}
								</Badge>
								<span className="text-sm text-muted-foreground font-normal">
									{stageDeals.length} deal{stageDeals.length !== 1 ? "s" : ""} &middot; {formatCurrency(stageDeals.reduce((s, d) => s + d.value, 0))}
								</span>
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-2">
								{stageDeals.map(deal => (
									<div key={deal.id} className="flex items-center justify-between py-2 border-b last:border-0">
										<div>
											<p className="text-sm font-medium">{deal.title}</p>
											<p className="text-xs text-muted-foreground">
												{formatCurrency(deal.value)}
												{deal.companyId && ` · ${companyMap.get(deal.companyId)}`}
											</p>
										</div>
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="ghost" size="icon" className="h-8 w-8">
													<MoreHorizontal className="h-4 w-4" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												<DropdownMenuItem onClick={() => openEdit(deal)}>
													<Pencil className="h-4 w-4 mr-2" /> Edit
												</DropdownMenuItem>
												{DEAL_STAGES.filter(s => s.value !== deal.stage).map(s => (
													<DropdownMenuItem key={s.value} onClick={() => handleStageChange(deal.id, s.value)}>
														Move to {s.label}
													</DropdownMenuItem>
												))}
												<DropdownMenuItem className="text-destructive" onClick={() => handleDelete(deal.id)}>
													<Trash2 className="h-4 w-4 mr-2" /> Delete
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				);
			})}

			{/* Create/Edit Dialog */}
			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{editing ? "Edit Deal" : "New Deal"}</DialogTitle>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="space-y-2">
							<Label>Deal Title</Label>
							<Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>Value ($)</Label>
								<Input
									type="number"
									value={form.value}
									onChange={e => setForm({ ...form, value: Number(e.target.value) })}
								/>
							</div>
							<div className="space-y-2">
								<Label>Stage</Label>
								<Select value={form.stage} onValueChange={v => setForm({ ...form, stage: v as DealStage })}>
									<SelectTrigger><SelectValue /></SelectTrigger>
									<SelectContent>
										{DEAL_STAGES.map(s => (
											<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
						<div className="space-y-2">
							<Label>Expected Close Date</Label>
							<Input
								type="date"
								value={form.expectedCloseDate}
								onChange={e => setForm({ ...form, expectedCloseDate: e.target.value })}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>Company</Label>
								<Select
									value={form.companyId ?? "none"}
									onValueChange={v => setForm({ ...form, companyId: v === "none" ? null : v })}
								>
									<SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
									<SelectContent>
										<SelectItem value="none">None</SelectItem>
										{companies?.map(c => (
											<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>Contact</Label>
								<Select
									value={form.contactId ?? "none"}
									onValueChange={v => setForm({ ...form, contactId: v === "none" ? null : v })}
								>
									<SelectTrigger><SelectValue placeholder="Select contact" /></SelectTrigger>
									<SelectContent>
										<SelectItem value="none">None</SelectItem>
										{contacts?.map(c => (
											<SelectItem key={c.id} value={c.id}>{c.firstName} {c.lastName}</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
						<div className="space-y-2">
							<Label>Description</Label>
							<Textarea
								value={form.description}
								onChange={e => setForm({ ...form, description: e.target.value })}
								rows={3}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
						<Button onClick={handleSave}>{editing ? "Save Changes" : "Create Deal"}</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
